// Dashboard del módulo POS. POS → Resumen → Dashboard.
import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { LucideIcon } from 'lucide-react';
import { AlertTriangle, Banknote, Boxes, LayoutDashboard, Loader2, Package, Percent, Receipt, ShieldCheck, Smartphone, Store, TrendingDown, TrendingUp } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { LocationOpt, locationsList, money, posRpc } from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface Periodo { actual: number; anterior: number; n?: number; abs: number; pct: number | null; proyeccion?: number; proyeccion_pct?: number | null; devoluciones?: number }
interface DashPos {
  kpis: {
    ventas_hoy: number; n_ventas_hoy: number; ticket_promedio: number; sesiones_abiertas: number;
    recargas_hoy: number; n_recargas_hoy: number; dif_pendientes: number; cierres_sin_deposito: number;
    recargas_pendientes: number; devoluciones_hoy: number; n_devoluciones_hoy: number;
  };
  periodos: { hoy: Periodo; mes: Periodo; anio: Periodo };
  serie_7d: { dia: string; total: number; n: number }[];
  formas_hoy: { forma: string; total: number }[];
  por_caja_hoy: { caja: string; n: number; total: number }[];
  top_productos_7d: { descripcion: string; unidades: number; total: number }[];
}

const num0 = (v: number | string) => Number(v).toLocaleString(LOCALE, { maximumFractionDigits: 0 });
// Formato compacto del eje Y (montos): $k para miles, $ con decimales para valores chicos.
const fmtY = (v: number) => (Number(v) >= 1000 ? `$${(Number(v) / 1000).toFixed(0)}k` : `$${Number(v).toLocaleString(LOCALE, { maximumFractionDigits: Number(v) < 10 && Number(v) % 1 !== 0 ? 2 : 0 })}`);

function Delta({ pct, abs }: { pct: number | null; abs: number }) {
  if (pct == null && Number(abs) === 0) return <span className="text-xs text-muted-foreground">sin referencia</span>;
  const up = Number(abs) >= 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${up ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
      {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
      {pct != null ? `${pct > 0 ? '+' : ''}${Number(pct).toLocaleString(LOCALE, { maximumFractionDigits: 1 })}%` : ''}
      <span className="text-muted-foreground font-normal">({Number(abs) >= 0 ? '+' : ''}{money(abs)})</span>
    </span>
  );
}

type RangoSerie = 'semana' | 'mes' | 'anio';
const RANGOS: { v: RangoSerie; label: string }[] = [
  { v: 'semana', label: 'Semana' }, { v: 'mes', label: 'Mes' }, { v: 'anio', label: 'Año' },
];
// Selector segmentado del rango de los gráficos (evolución de ventas + horario)
function SelectorRango({ rango, setRango }: { rango: RangoSerie; setRango: (r: RangoSerie) => void }) {
  return (
    <span className="ml-auto inline-flex overflow-hidden rounded-md border text-[11px] font-normal">
      {RANGOS.map((r) => (
        <button key={r.v} onClick={() => setRango(r.v)}
                className={`px-2 py-0.5 ${rango === r.v ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}>
          {r.label}
        </button>
      ))}
    </span>
  );
}

export interface Tile { icon: LucideIcon; label: string; valor: string; sub: string; alerta?: boolean }

export function KpiTile({ t }: { t: Tile }) {
  return (
    <Card className={t.alerta ? 'border-amber-400' : ''}>
      <CardContent className="pt-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground"><t.icon className="w-4 h-4" /> {t.label}</div>
        <div className={`text-2xl font-semibold font-mono tabular-nums mt-1 ${t.alerta ? 'text-amber-600 dark:text-amber-400' : ''}`}>{t.valor}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{t.sub}</div>
      </CardContent>
    </Card>
  );
}

// `titulo`/`extra`: el dashboard del POS-Restaurant lo reutiliza TAL CUAL y
// añade sus cards propios (extra se pinta después de los KPIs del POS).
export function DashboardPos({ titulo = 'Dashboard del POS', extra, modo = 'pos' }: { titulo?: string; extra?: ReactNode; modo?: 'pos' | 'restaurant' } = {}) {
  const [local, setLocal] = useState('');
  const [rango, setRango] = useState<RangoSerie>('semana');
  const diasRango = rango === 'semana' ? 7 : rango === 'mes' ? 30 : 365;
  const { data: locales = [] } = useQuery<LocationOpt[]>({ queryKey: ['pos-locales-dash'], queryFn: locationsList });
  const { data, isFetching } = useQuery<DashPos>({
    queryKey: ['pos-dashboard', local], queryFn: () => posRpc('pos_dashboard', { _location: local || null }),
    staleTime: 0, refetchOnMount: 'always',
  });
  const { data: horario = [] } = useQuery<HoraVenta[]>({
    queryKey: ['pos-dashboard-horario', local, rango],
    queryFn: () => posRpc('pos_dashboard_horario', { _location: local || null, _dias: diasRango }),
    staleTime: 0, refetchOnMount: 'always',
  });
  // Serie de evolución según el rango elegido (semana=7d, mes=30d, año=12 meses)
  const { data: serie = [] } = useQuery<{ dia: string; total: number; n: number }[]>({
    queryKey: ['pos-dashboard-serie', local, rango],
    queryFn: () => posRpc('pos_dashboard_serie', { _location: local || null, _rango: rango }),
    staleTime: 0, refetchOnMount: 'always',
  });
  const { data: valz } = useQuery<{ productos: number; unidades: number; valor: number }>({
    queryKey: ['pos-valorizacion-local', local],
    queryFn: () => posRpc('pos_valorizacion_local', { _location: local || null }),
    staleTime: 0, refetchOnMount: 'always',
  });
  const { data: vk } = useQuery<{ ventas_neto: number; unidades: number; costo: number; margen: number; margen_pct: number; ticket_promedio: number; items_ticket: number; n_ventas: number }>({
    queryKey: ['pos-kpis-ventas', local],
    queryFn: () => posRpc('pos_kpis_ventas', { _location: local || null }),
    staleTime: 0, refetchOnMount: 'always',
  });
  const { data: emision } = useQuery<{ autorizados: number; pendientes: number; rechazados: number }>({
    queryKey: ['pos-dashboard-emision', local],
    queryFn: () => posRpc('pos_dashboard_emision', { _location: local || null }),
    staleTime: 0, refetchOnMount: 'always',
  });

  if (!data) {
    return (
      <div className="p-6 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" /> Cargando dashboard…
      </div>
    );
  }
  const k = data.kpis;
  const alertas = Number(k.dif_pendientes) + Number(k.cierres_sin_deposito) + Number(k.recargas_pendientes);

  // En modo 'restaurant' se omiten los tiles de retail (recargas, conciliación,
  // valorización, margen y unidades): el restaurante trae sus propios cards.
  const tiles: Tile[] = [
    { icon: Receipt, label: 'Ventas netas de hoy', valor: money(k.ventas_hoy), sub: `${num0(k.n_ventas_hoy)} tickets · promedio ${money(k.ticket_promedio)}${Number(k.devoluciones_hoy) > 0 ? ` · dev. −${money(k.devoluciones_hoy)} (${num0(k.n_devoluciones_hoy)})` : ''}`, alerta: Number(k.devoluciones_hoy) > 0 },
    { icon: Store, label: 'Sesiones abiertas', valor: num0(k.sesiones_abiertas), sub: 'cajas operando ahora' },
    ...(modo === 'restaurant' ? [] : [
      { icon: Smartphone, label: 'Recargas de hoy', valor: money(k.recargas_hoy), sub: `${num0(k.n_recargas_hoy)} transacciones Broadnet`, alerta: Number(k.recargas_pendientes) > 0 },
      { icon: Banknote, label: 'Por conciliar', valor: num0(Number(k.dif_pendientes) + Number(k.cierres_sin_deposito)), sub: `${num0(k.dif_pendientes)} dif. de arqueo · ${num0(k.cierres_sin_deposito)} sin depósito`, alerta: alertas > 0 },
      { icon: Boxes, label: local ? 'Valorización de la sucursal (costo)' : 'Valorización inventario (costo)', valor: money(valz?.valor ?? 0), sub: `${num0(valz?.productos ?? 0)} productos · ${num0(valz?.unidades ?? 0)} u.${local ? '' : ' · consolidado'}` },
      { icon: Percent, label: 'Margen bruto de hoy', valor: money(vk?.margen ?? 0), sub: `${vk?.margen_pct ?? 0}% margen · costo ${money(vk?.costo ?? 0)}` },
      { icon: Package, label: 'Unidades vendidas hoy', valor: num0(vk?.unidades ?? 0), sub: `${vk?.items_ticket ?? 0} ítems por ticket` },
    ] as Tile[]),
  ];

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <LayoutDashboard className="w-5 h-5" /> {titulo}
          {isFetching && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
        </h1>
        <select
          className="h-10 w-64 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
        >
          <option value="">Todos los locales (consolidado)</option>
          {locales.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
        </select>
      </div>

      {Number(k.recargas_pendientes) > 0 && (
        <div className="flex items-center gap-2 rounded-md bg-pendiente-soft text-pendiente px-3 py-2 text-sm">
          <AlertTriangle className="w-4 h-4" />
          {num0(k.recargas_pendientes)} recarga(s) Broadnet pendiente(s) de regularizar — la caja afectada está bloqueada para nuevas recargas.
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3">
        {tiles.map((t) => <KpiTile key={t.label} t={t} />)}
        {emision && (
          <Card className={Number(emision.pendientes) > 0 || Number(emision.rechazados) > 0 ? 'border-amber-400' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Facturas al SRI</div>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="text-2xl font-semibold tabular-nums text-emerald-600">{num0(emision.autorizados)}</span>
                <span className="text-xs text-muted-foreground">aut.</span>
                <span className="mx-1 text-muted-foreground/40">/</span>
                <span className={`text-2xl font-semibold tabular-nums ${Number(emision.pendientes) > 0 ? 'text-amber-600' : 'text-muted-foreground'}`}>{num0(emision.pendientes)}</span>
                <span className="text-xs text-muted-foreground">pend.</span>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">{Number(emision.rechazados) > 0 ? `${num0(emision.rechazados)} rechazadas/devueltas` : 'autorizadas / por autorizar'}</div>
            </CardContent>
          </Card>
        )}
      </div>

      {extra}

      {/* Acumulado de ventas: HOY · MES · AÑO (resumen ejecutivo; reusa periodos, cero backend) */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-3">
            <TrendingUp className="w-4 h-4 text-primary" /> Acumulado de ventas · {local ? 'sucursal' : 'consolidado'}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 divide-y sm:divide-y-0 sm:divide-x divide-border">
            {/* HOY (protagonista) */}
            <div className="pb-3 sm:pb-0 sm:pr-4">
              <div className="text-xs font-medium text-muted-foreground">HOY</div>
              <div className="text-3xl font-bold font-mono tabular-nums mt-0.5">{money(data.periodos.hoy.actual)}</div>
              <Delta pct={data.periodos.hoy.pct} abs={data.periodos.hoy.abs} />
              <div className="text-xs text-muted-foreground mt-0.5">Ayer: {money(data.periodos.hoy.anterior)}</div>
            </div>
            {/* MES (protagonista) */}
            <div className="py-3 sm:py-0 sm:px-4">
              <div className="text-xs font-medium text-muted-foreground">MES actual{Number(data.periodos.mes.n ?? 0) ? ` · ${num0(data.periodos.mes.n ?? 0)} ventas` : ''}</div>
              <div className="text-3xl font-bold font-mono tabular-nums mt-0.5">{money(data.periodos.mes.actual)}</div>
              <Delta pct={data.periodos.mes.pct} abs={data.periodos.mes.abs} />
              <div className="text-xs text-muted-foreground mt-0.5">
                Mes ant.: {money(data.periodos.mes.anterior)} · proy. <span className="font-medium text-foreground/80">{money(data.periodos.mes.proyeccion ?? 0)}</span>
                {data.periodos.mes.proyeccion_pct != null && <span className={`ml-1 ${Number(data.periodos.mes.proyeccion_pct) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>({Number(data.periodos.mes.proyeccion_pct) > 0 ? '+' : ''}{data.periodos.mes.proyeccion_pct}%)</span>}
              </div>
            </div>
            {/* AÑO (secundario, más discreto) */}
            <div className="pt-3 sm:pt-0 sm:pl-4">
              <div className="text-xs font-medium text-muted-foreground">AÑO{Number(data.periodos.anio.n ?? 0) ? ` · ${num0(data.periodos.anio.n ?? 0)} ventas` : ''}</div>
              <div className="text-2xl font-semibold font-mono tabular-nums mt-0.5 text-foreground/75">{money(data.periodos.anio.actual)}</div>
              <Delta pct={data.periodos.anio.pct} abs={data.periodos.anio.abs} />
              <div className="text-xs text-muted-foreground mt-0.5">
                Año ant.: {money(data.periodos.anio.anterior)} · proy. <span className="font-medium text-foreground/80">{money(data.periodos.anio.proyeccion ?? 0)}</span>
                {data.periodos.anio.proyeccion_pct != null && <span className={`ml-1 ${Number(data.periodos.anio.proyeccion_pct) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>({Number(data.periodos.anio.proyeccion_pct) > 0 ? '+' : ''}{data.periodos.anio.proyeccion_pct}%)</span>}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-3">
            <span>Ventas — {rango === 'semana' ? 'últimos 7 días' : rango === 'mes' ? 'mes actual (día a día)' : `año ${new Date().getFullYear()} (por mes)`}</span>
            <SelectorRango rango={rango} setRango={setRango} />
          </CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={serie.map((d) => ({
                  dia: rango === 'anio'
                    ? new Date(d.dia + 'T12:00:00').toLocaleDateString(LOCALE, { month: 'short', year: '2-digit' })
                    : rango === 'mes'
                      ? new Date(d.dia + 'T12:00:00').toLocaleDateString(LOCALE, { day: 'numeric', month: 'short' })
                      : new Date(d.dia + 'T12:00:00').toLocaleDateString(LOCALE, { weekday: 'short', day: 'numeric' }),
                  Ventas: Number(d.total),
                }))} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="dia" fontSize={11} />
                <YAxis fontSize={11} width={54} tickFormatter={fmtY} />
                <Tooltip formatter={(v: number) => money(v)} cursor={{ fill: 'currentColor', fillOpacity: 0.06 }} />
                <Bar dataKey="Ventas" fill="#10b981" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <VentasPorHorario horario={horario} dias={diasRango} />

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Hoy por forma de pago y caja</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <table className="w-full text-sm self-start">
              <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-1.5 pr-2">Forma</th><th className="py-1.5 text-right">Total</th></tr></thead>
              <tbody>
                {data.formas_hoy.map((f) => (
                  <tr key={f.forma} className="border-b last:border-0">
                    <td className="py-1.5 pr-2">{f.forma}</td>
                    <td className="py-1.5 text-right font-mono tabular-nums">{money(f.total)}</td>
                  </tr>
                ))}
                {!data.formas_hoy.length && <tr><td colSpan={2} className="py-4 text-center text-muted-foreground">Sin ventas hoy</td></tr>}
              </tbody>
            </table>
            <table className="w-full text-sm self-start">
              <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-1.5 pr-2">Caja</th><th className="py-1.5 text-right">Total</th></tr></thead>
              <tbody>
                {data.por_caja_hoy.map((c) => (
                  <tr key={c.caja} className="border-b last:border-0">
                    <td className="py-1.5 pr-2">{c.caja} <span className="text-xs text-muted-foreground">({num0(c.n)})</span></td>
                    <td className="py-1.5 text-right font-mono tabular-nums">{money(c.total)}</td>
                  </tr>
                ))}
                {!data.por_caja_hoy.length && <tr><td colSpan={2} className="py-4 text-center text-muted-foreground">Sin ventas hoy</td></tr>}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Top 10 productos — últimos 7 días</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Producto</th>
              <th className="py-2 pr-3 text-right">Unidades</th><th className="py-2 pr-3 text-right">Total</th>
            </tr></thead>
            <tbody>
              {data.top_productos_7d.map((p, i) => (
                <tr key={p.descripcion} className="border-b last:border-0">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3">{p.descripcion}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{num0(p.unidades)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(p.total)}</td>
                </tr>
              ))}
              {!data.top_productos_7d.length && <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">Sin ventas en la semana</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

interface HoraVenta { hora: number; hoy_total: number; hoy_n: number; prom_total: number }

// Ventas por horario: barras de HOY hora a hora con el promedio diario por hora de
// los últimos 30 días como referencia (misma barra, atenuada).
function VentasPorHorario({ horario, dias = 30 }: { horario: HoraVenta[]; dias?: number }) {
  const activas = horario.filter((h) => Number(h.hoy_total) > 0 || Number(h.prom_total) > 0);
  const desde = activas.length ? Math.min(...activas.map((h) => h.hora)) : 8;
  const hasta = activas.length ? Math.max(...activas.map((h) => h.hora)) : 20;
  const rango = horario.filter((h) => h.hora >= desde && h.hora <= hasta);
  const pico = activas.reduce<HoraVenta | null>((p, h) => (Number(h.hoy_total) > Number(p?.hoy_total ?? 0) ? h : p), null);
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-3">
        <span>Ventas por horario — hoy</span>
        <span className="ml-auto flex items-center gap-3 text-[11px] font-normal text-muted-foreground">
          <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm" style={{ background: '#2563eb' }} /> Hoy</span>
          <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm" style={{ background: '#cbd5e1' }} /> Promedio {dias} días</span>
        </span>
      </CardTitle></CardHeader>
      <CardContent>
        {activas.length === 0 ? (
          <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">Sin ventas registradas hoy ni en los últimos {dias} días</div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={rango.map((h) => ({ hora: `${h.hora}h`, Hoy: Number(h.hoy_total), [`Prom. ${dias}d`]: Number(h.prom_total) }))} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="hora" fontSize={11} />
                <YAxis fontSize={11} width={54} tickFormatter={fmtY} />
                <Tooltip formatter={(v: number) => money(v)} cursor={{ fill: 'currentColor', fillOpacity: 0.06 }} />
                <Bar dataKey="Hoy" fill="#2563eb" radius={[3, 3, 0, 0]} />
                <Bar dataKey={`Prom. ${dias}d`} fill="#cbd5e1" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            {pico && Number(pico.hoy_total) > 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                Hora pico de hoy: <span className="font-medium text-foreground">{String(pico.hora).padStart(2, '0')}:00</span> con {money(pico.hoy_total)} ({num0(pico.hoy_n)} tickets)
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
